import { FC, useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { PatientPageNavBar } from './PatientPageNavBar'
import styles from './PatientLayout.module.scss'
import { PatientInfos } from '../../patient'
import { FullLoader, Icon } from '../../shared'
import { MedicalEventNavBar } from './MedicalEventNavBar'
import classNames from 'classnames/bind'
import { useLocalStorage } from 'react-use'
import { isDefined } from '../../../misc/functions.utilities'
import { getV3ColorCssVariable } from '../../../design-system/colors_v3'
import { PatientLayoutContext } from './PatientLayout.context'
import { useCurrentPatient } from '../../../hooks/utils'
import { AIConsultation } from '../../AIConsultation'
import { useUserEnabledFeature } from '../../../hooks/utils/user'
import { FeaturesType } from '@follow/cdk'

const cx = classNames.bind(styles)

export const PatientLayout: FC = () => {
  const { currentPatient: patient } = useCurrentPatient()
  const { medicalEventId } = useParams<'medicalEventId'>()
  const [openInformations, setOpenInformations] = useLocalStorage<boolean>(
    'ui.medicalEvent.userInformationsPanel.open',
    false,
  )

  const consultationAssistantEnabled = useUserEnabledFeature(FeaturesType.consultationAssistant)

  const [openAIConsultation, setOpenAIConsultation] = useLocalStorage<boolean>(
    'ui.medicalEvent.aiConsultation.open',
    consultationAssistantEnabled!!,
  )

  const [ignoredAlertCodes, setIgnoredAlertCodes] = useState<string[]>([])
  const foldable = isDefined(medicalEventId)
  const foldableAIConsultation = isDefined(consultationAssistantEnabled)
  const hidePanel = !openInformations && foldable
  const hidePanelAIConsultation =
    !consultationAssistantEnabled || (!openAIConsultation && foldableAIConsultation)

  return (
    <PatientLayoutContext.Provider value={{ ignoredAlertCodes, setIgnoredAlertCodes }}>
      {medicalEventId ? <MedicalEventNavBar /> : <PatientPageNavBar />}

      <div className={styles.container}>
        <div
          className={cx(
            styles.sidePanel,
            styles.patientInfo,
            {
              foldable,
              medicalEventContext: !!medicalEventId,
            },
            hidePanel ? styles.hide : styles.show,
          )}
        >
          {patient ? (
            <PatientInfos patient={patient} key={patient.id} />
          ) : (
            <div className={styles.loading}>
              <FullLoader />
            </div>
          )}
          {foldable && (
            <div
              className={cx('grip', 'left')}
              onClick={() => {
                document.dispatchEvent(new Event('layout.resize'))
                setOpenInformations(!openInformations)
              }}
              title={openInformations ? 'Fermer le panneau' : 'Ouvrir le panneau'}
            >
              <Icon
                icon="caret"
                size="micro"
                color={getV3ColorCssVariable('shades', 'white')}
                rotate={!openInformations ? 90 : -90}
              />
            </div>
          )}
        </div>
        <div className={cx(styles.sidePanelDummy, hidePanel ? styles.shrink : styles.expand)}></div>
        <div className={cx(styles.content)}>
          <Outlet />
        </div>
        {consultationAssistantEnabled && patient && medicalEventId && (
          <>
            <div
              className={cx(
                styles.sidePanel,
                styles.aiConsultation,
                {
                  medicalEventContext: !!medicalEventId,
                },
                hidePanelAIConsultation ? styles.hide : styles.show,
              )}
            >
              <AIConsultation />
              {foldableAIConsultation && (
                <div
                  className={cx('grip', 'right')}
                  onClick={() => {
                    document.dispatchEvent(new Event('layout.resize'))
                    setOpenAIConsultation(!openAIConsultation)
                  }}
                  title={openAIConsultation ? 'Fermer le panneau' : 'Ouvrir le panneau'}
                >
                  <Icon
                    icon="caret"
                    size="micro"
                    color={getV3ColorCssVariable('shades', 'white')}
                    rotate={!openAIConsultation ? -90 : 90}
                  />
                </div>
              )}
            </div>
            <div
              className={cx(
                styles.sidePanelDummy,
                hidePanelAIConsultation ? styles.shrink : styles.expand,
              )}
            ></div>
          </>
        )}
      </div>
    </PatientLayoutContext.Provider>
  )
}
